// src/pages/finance-portal/bank-reconciliation/ReconciliationHub.js
//
// Bank Reconciliation hub: KPIs + list + start-new dialog.

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Alert, Box, Button, Card, CardContent, Checkbox, Chip, CircularProgress,
    Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
    Grid, IconButton, MenuItem, Skeleton, Stack, Table, TableBody, TableCell,
    TableHead, TablePagination, TableRow, TextField, Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InventoryIcon from '@mui/icons-material/Inventory';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#3b82f6';

const STATUS_COLORS = {
    DRAFT: { bg: '#f1f5f9', color: '#475569' },
    IN_REVIEW: { bg: '#fef3c7', color: '#92400e' },
    EXCEPTION: { bg: '#fee2e2', color: '#991b1b' },
    RECONCILED: { bg: '#dcfce7', color: '#166534' },
    CLOSED: { bg: '#dbeafe', color: '#1e40af' },
};

function formatCurrency(amount) {
    if (amount == null) return '$0.00';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
}

const EMPTY_START = {
    stripe_account_id: '',
    statement_period_start: '',
    statement_period_end: '',
    statement_beginning_balance: '',
    statement_ending_balance: '',
    auto_match: true,
};

export default function ReconciliationHub() {
    const navigate = useNavigate();
    const [dashboard, setDashboard] = useState(null);
    const [list, setList] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [perPage, setPerPage] = useState(20);
    const [statusFilter, setStatusFilter] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [errors, setErrors] = useState({});
    const [startDialog, setStartDialog] = useState({ open: false, form: { ...EMPTY_START }, saving: false });

    const fetchAll = useCallback(async () => {
        setError(null);
        setIsLoading(true);
        try {
            const params = { page: page + 1, per_page: perPage };
            if (statusFilter) params.status = statusFilter;
            const [dashRes, listRes, acctRes] = await Promise.all([
                axiosInstance.get('/api/finance/reconciliations/dashboard'),
                axiosInstance.get('/api/finance/reconciliations', { params }),
                axiosInstance.get('/api/finance/stripe/accounts').catch(() => ({ data: { data: [] } })),
            ]);
            if (dashRes.data?.success) setDashboard(dashRes.data.data);
            if (listRes.data?.success) {
                setList(listRes.data.data || []);
                setTotal(listRes.data.total || 0);
            }
            setAccounts(acctRes.data?.data || []);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load reconciliations.');
        } finally {
            setIsLoading(false);
        }
    }, [page, perPage, statusFilter]);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    const handleStart = async () => {
        setStartDialog({ ...startDialog, saving: true });
        setErrors({});
        try {
            const res = await axiosInstance.post('/api/finance/reconciliations', startDialog.form);
            const recId = res.data?.data?.id;
            setStartDialog({ open: false, form: { ...EMPTY_START }, saving: false });
            if (recId) navigate(`/finance-portal/bank-reconciliation/${recId}`);
            else fetchAll();
        } catch (err) {
            const serverErrors = err.response?.data?.errors;
            const msg = err.response?.data?.message || 'Could not start reconciliation.';
            if (serverErrors && typeof serverErrors === 'object') {
                setErrors(serverErrors);
            }
            setError(msg);
            setStartDialog({ ...startDialog, saving: false });
        }
    };

    const totals = dashboard?.totals || {};

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, fontFamily, minHeight: '100vh', bgcolor: '#f8fafc' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Box>
                    <Typography sx={{ fontFamily, fontWeight: 800, fontSize: '1.6rem', color: '#0f172a' }}>
                        Bank Reconciliation
                    </Typography>
                    <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#64748b', mt: 0.5 }}>
                        AI-assisted GL ↔ bank matching with full exception workflow.
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1.5}>
                    <Button
                        variant="outlined"
                        startIcon={<InventoryIcon />}
                        onClick={() => navigate('/finance-portal/bank-reconciliation/archive')}
                        sx={{ fontFamily, textTransform: 'none', borderRadius: '10px' }}
                    >
                        Archive
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setStartDialog({ open: true, form: { ...EMPTY_START }, saving: false })}
                        sx={{ fontFamily, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT }}
                    >
                        Start Reconciliation
                    </Button>
                    <IconButton onClick={fetchAll}><RefreshIcon /></IconButton>
                </Stack>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {/* KPIs */}
            <Grid container spacing={2.5} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={3}>
                    <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                        <CardContent sx={{ p: 2.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <HourglassEmptyIcon sx={{ color: '#f59e0b', fontSize: 18 }} />
                                <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase' }}>Open</Typography>
                            </Box>
                            <Typography sx={{ fontFamily, fontWeight: 800, fontSize: '1.5rem', mt: 0.5 }}>
                                {isLoading ? <Skeleton width={40} /> : (totals.open_count || 0)}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                        <CardContent sx={{ p: 2.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <CompareArrowsIcon sx={{ color: ACCENT, fontSize: 18 }} />
                                <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase' }}>Matched Amount</Typography>
                            </Box>
                            <Typography sx={{ fontFamily, fontWeight: 800, fontSize: '1.5rem', mt: 0.5 }}>
                                {isLoading ? <Skeleton width={120} /> : formatCurrency(totals.matched_amount)}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                        <CardContent sx={{ p: 2.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <ErrorOutlineIcon sx={{ color: '#ef4444', fontSize: 18 }} />
                                <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase' }}>Open Exceptions</Typography>
                            </Box>
                            <Typography sx={{ fontFamily, fontWeight: 800, fontSize: '1.5rem', mt: 0.5 }}>
                                {isLoading ? <Skeleton width={40} /> : (totals.open_exception_count || 0)}
                            </Typography>
                            {(totals.aged_exception_count || 0) > 0 && (
                                <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#ef4444', mt: 0.5 }}>
                                    {totals.aged_exception_count} past SLA
                                </Typography>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                        <CardContent sx={{ p: 2.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <CheckCircleIcon sx={{ color: '#10b981', fontSize: 18 }} />
                                <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase' }}>Completion Rate</Typography>
                            </Box>
                            <Typography sx={{ fontFamily, fontWeight: 800, fontSize: '1.5rem', mt: 0.5 }}>
                                {isLoading ? <Skeleton width={60} /> : `${totals.completion_rate_pct || 0}%`}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Filters + List */}
            <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                <CardContent sx={{ p: 0 }}>
                    <Box sx={{ p: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0', flexWrap: 'wrap', gap: 1 }}>
                        <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1rem' }}>Reconciliations</Typography>
                        <TextField
                            select
                            size="small"
                            label="Status"
                            value={statusFilter}
                            onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
                            sx={{ minWidth: 180, '& .MuiInputBase-root': { fontFamily } }}
                        >
                            <MenuItem value="">All</MenuItem>
                            {Object.keys(STATUS_COLORS).map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                        </TextField>
                    </Box>
                    {isLoading ? (
                        <Box sx={{ p: 3 }}><Skeleton variant="rounded" height={300} /></Box>
                    ) : (
                        <>
                            <Table>
                                <TableHead>
                                    <TableRow sx={{ '& th': { fontFamily, fontWeight: 700, color: '#475569', bgcolor: '#f8fafc' } }}>
                                        <TableCell>Account</TableCell>
                                        <TableCell>Period</TableCell>
                                        <TableCell align="right">Statement Balance</TableCell>
                                        <TableCell align="right">Variance</TableCell>
                                        <TableCell align="right">Matched</TableCell>
                                        <TableCell align="right">Unmatched</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell align="right" />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {list.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={8} sx={{ textAlign: 'center', py: 5, fontFamily, color: '#94a3b8' }}>
                                                No reconciliations yet.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {list.map((r) => (
                                        <TableRow key={r.id} hover>
                                            <TableCell sx={{ fontFamily }}>
                                                <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem' }}>
                                                    {r.account_name || `Account #${r.stripe_account_id}`}
                                                </Typography>
                                                <Typography sx={{ fontFamily, fontSize: '0.75rem', color: '#94a3b8' }}>
                                                    {r.institution_name} {r.account_mask ? `••••${r.account_mask}` : ''}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ fontFamily, fontSize: '0.85rem' }}>
                                                {r.statement_period_start} → {r.statement_period_end}
                                            </TableCell>
                                            <TableCell align="right" sx={{ fontFamily, fontWeight: 700 }}>
                                                {formatCurrency(r.statement_ending_balance)}
                                            </TableCell>
                                            <TableCell align="right" sx={{
                                                fontFamily, fontWeight: 700,
                                                color: Math.abs(r.variance || 0) < 0.01 ? '#10b981' : '#ef4444',
                                            }}>
                                                {formatCurrency(r.variance)}
                                            </TableCell>
                                            <TableCell align="right" sx={{ fontFamily }}>
                                                {r.matched_count} ({formatCurrency(r.matched_amount)})
                                            </TableCell>
                                            <TableCell align="right" sx={{ fontFamily, color: '#f59e0b' }}>
                                                {r.unmatched_bank_count + r.unmatched_gl_count}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={r.status}
                                                    size="small"
                                                    sx={{
                                                        fontFamily, fontWeight: 700, fontSize: '0.7rem',
                                                        bgcolor: STATUS_COLORS[r.status]?.bg, color: STATUS_COLORS[r.status]?.color,
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell align="right">
                                                <IconButton size="small" onClick={() => navigate(`/finance-portal/bank-reconciliation/${r.id}`)}>
                                                    <VisibilityIcon fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                            <TablePagination
                                component="div"
                                count={total}
                                page={page}
                                onPageChange={(_, p) => setPage(p)}
                                rowsPerPage={perPage}
                                onRowsPerPageChange={(e) => { setPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                                rowsPerPageOptions={[10, 20, 50, 100]}
                                sx={{ '& .MuiTablePagination-toolbar': { fontFamily } }}
                            />
                        </>
                    )}
                </CardContent>
            </Card>

            <Dialog open={startDialog.open} onClose={() => setStartDialog({ open: false, form: { ...EMPTY_START }, saving: false })} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Start Reconciliation</DialogTitle>
                <DialogContent>
                    <Stack spacing={2.5} sx={{ mt: 1 }}>
                        <TextField
                            select
                            label="Bank Account"
                            value={startDialog.form.stripe_account_id || ''}
                            onChange={(e) => setStartDialog({ ...startDialog, form: { ...startDialog.form, stripe_account_id: e.target.value } })}
                            fullWidth
                        >
                            {accounts.map(a => (
                                <MenuItem key={a.id} value={a.id}>
                                    {a.name || a.official_name || 'Account'} {a.mask ? `••••${a.mask}` : ''}
                                </MenuItem>
                            ))}
                        </TextField>
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <TextField
                                    label="Period Start"
                                    type="date"
                                    value={startDialog.form.statement_period_start}
                                    onChange={(e) => setStartDialog({ ...startDialog, form: { ...startDialog.form, statement_period_start: e.target.value } })}
                                    InputLabelProps={{ shrink: true }}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    label="Period End"
                                    type="date"
                                    value={startDialog.form.statement_period_end}
                                    onChange={(e) => setStartDialog({ ...startDialog, form: { ...startDialog.form, statement_period_end: e.target.value } })}
                                    InputLabelProps={{ shrink: true }}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    label="Statement Beginning Balance"
                                    type="number"
                                    value={startDialog.form.statement_beginning_balance}
                                    onChange={(e) => setStartDialog({ ...startDialog, form: { ...startDialog.form, statement_beginning_balance: e.target.value } })}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    label="Statement Ending Balance"
                                    type="number"
                                    value={startDialog.form.statement_ending_balance}
                                    onChange={(e) => setStartDialog({ ...startDialog, form: { ...startDialog.form, statement_ending_balance: e.target.value } })}
                                    fullWidth
                                />
                            </Grid>
                        </Grid>
                        <FormControlLabel
                            control={(
                                <Checkbox
                                    checked={startDialog.form.auto_match}
                                    onChange={(e) => setStartDialog({ ...startDialog, form: { ...startDialog.form, auto_match: e.target.checked } })}
                                />
                            )}
                            label="Run auto-match immediately"
                            sx={{ '& .MuiFormControlLabel-label': { fontFamily } }}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setStartDialog({ open: false, form: { ...EMPTY_START }, saving: false })} sx={{ fontFamily, textTransform: 'none' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleStart}
                        disabled={!startDialog.form.stripe_account_id || !startDialog.form.statement_period_end || startDialog.saving}
                        startIcon={startDialog.saving ? <CircularProgress size={14} sx={{ color: 'white' }} /> : null}
                        sx={{ fontFamily, textTransform: 'none', bgcolor: ACCENT }}
                    >
                        Start
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
